import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const ACTION_SEARCH_FIELDS = ['name'];
const ACTION_SORT_OPTIONS = [
  { key: 'name', label: 'Name', accessor: (a) => a.name?.toLowerCase() ?? '' },
];

const ActionComponent = () => {
  const [actions, setActions] = useState([]);
  const [currentAction, setCurrentAction] = useState({ name: '' });
  const [showForm, setShowForm] = useState(false);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { authState } = useAuth();
  const navigate = useNavigate();

  const {
    items: visibleActions,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(actions, {
    searchFields: ACTION_SEARCH_FIELDS,
    sortOptions: ACTION_SORT_OPTIONS,
  });

  useEffect(() => {
    if (!authState) {
      navigate('/login');
    }
  }, [authState, navigate]);

  useEffect(() => {
    fetchActions();
  }, []);

  const fetchActions = async () => {
    const response = await fetch(`${backendURL}/api/actions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    const data = await response.json();
    setActions(data);
  };

  const handleFormChange = (e) => {
    setCurrentAction({ ...currentAction, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentAction.name || currentAction.name.trim() === '') {
      alert("Name can't be empty"); // Simple validation feedback
      return;
    }

    if (currentAction.action_id) {
      await updateAction(currentAction.action_id, currentAction);
    } else {
      await createAction(currentAction);
    }
    setCurrentAction({ name: '' });
    setShowForm(false);
    fetchActions();
  };

  const createAction = async (actionData) => {
    await fetch(`${backendURL}/api/actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(actionData),
      credentials: 'include',
    });
  };

  const updateAction = async (actionId, actionData) => {
    await fetch(`${backendURL}/api/actions/${actionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(actionData),
      credentials: 'include',
    });
  };

  const deleteAction = async () => {
    if (actionToDelete) {
      await fetch(`${backendURL}/api/actions/${actionToDelete}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setActionToDelete(null);
      setShowModal(false);
      fetchActions();
    }
  };

  const editAction = (action) => {
    setCurrentAction(action);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentAction({ name: '' });
    setShowForm(false);
  };

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentAction({ name: '' });
    }
    setShowForm(!showForm);
  };

  const actionForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="name">
          <Form.Label>Action Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter action name"
            name="name"
            onChange={handleFormChange}
            value={currentAction.name}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Action"
        listLabel="Show Actions List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Actions"
        form={actionForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="action"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search actions" placeholder="Action name" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No actions have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleActions.map((action) => (
          <ExpandableRow
            key={action.action_id}
            main={{ title: action.name }}
            actions={(
              <>
                <Button size="sm" onClick={() => editAction(action)} aria-label="Edit action">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setActionToDelete(action.action_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete action"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteAction}
        bodyText="Are you sure you want to delete this action?"
      />
    </div>
  );
};

export default ActionComponent;
